import React, { useEffect, useRef, useState } from 'react';
import { webLoadingFalse, webNavigate } from '../../services/navigate.service';
import CloseIcon from '../../images/CloseIcon';
import { deleteAttribute, getListAttributes } from '../../services/attributes.service';
import { showDeleteToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';
import PlusIconWhite from '../../images/PlusIconWhite';
import EditIconV2 from '../../images/EditIconV2';
import DeleteIconV2 from '../../images/DeleteIconV2';
import { useResponsive } from '../../hook/useResponsive';
import AttributesIcon from '../../images/AttributesIcon';
import {
  ListColumn,
  ListQuery,
  ListRowAction,
  ListScreen,
  ListScreenHandle,
  cells,
  t,
} from '../../components/list';

interface AttributeRow {
  id: string;
  attributeName: string;
  displayName: string;
  type: number;
  updatedAt: Date;
  isActive: boolean;
}

interface AttributesResponse {
  data: {
    data: {
      attributes: AttributeRow[];
      count: number;
    };
  };
}

const Attributes = () => {
  const preLanguage = localStorage.getItem('language');
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<ListScreenHandle>(null);
  const [language, setLanguage] = useState<any>(null);
  const companyId: string = localStorage.getItem('companyId') || '';
  const [showDelete, setShowDelete] = useState<boolean>(false);
  const [idDelete, setIdDelete] = useState<string>('');
  const inforTable: any = {
    title:
      language && language?.['No attributes currently']
        ? language?.['No attributes currently']
        : 'No attributes currently',
    path: '/attributes/',
    desc:
      language && language?.['You haven’t added any attributes yet. Start by adding a new one']
        ? language?.['You haven’t added any attributes yet. Start by adding a new one']
        : 'You haven’t added any attributes yet. Start by adding a new one',
    titleCreate:
      language && language?.['New Attributes'] ? language?.['New Attributes'] : 'New Attributes',
    img: <AttributesIcon />
  };
  const { isMobile } = useResponsive();

  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
      event.preventDefault();
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  /** One page of attributes; the search box maps onto the endpoint's `searchValue`. */
  const fetchAttributes = async (query: ListQuery) => {
    webLoadingFalse();
    const response: AttributesResponse = await getListAttributes(
      companyId,
      query.page,
      query.limit,
      query.search
    );
    return {
      rows: response.data.data.attributes ?? [],
      count: response.data.data.count ?? 0,
    };
  };

  const goToAttribute = (id: string) => webNavigate('/' + preLanguage + '/attributes/' + id);

  const handleShowDelete = (id: string) => {
    setShowDelete(true);
    setIdDelete(id);
  };
  const handleDeleteEmployee = async () => {
    try {
      const response = await deleteAttribute(idDelete);
      showDeleteToast(language?.['Attribute Deleted !'] ?? 'Attribute Deleted !');
    } catch (error) {
      console.error('Error loading terms:', error);
      showApiError(error, { language });
    } finally {
      listRef.current?.reload();
      setShowDelete(false);
    }
  };

  /* ---------------- List kit configuration ---------------- */

  const columns: ListColumn<AttributeRow>[] = [
    {
      key: 'attributeName',
      label: 'Attribute name',
      width: 260,
      hideable: false,
      render: cells.primary(a => a.attributeName),
      mobile: 'title',
      onClick: a => goToAttribute(String(a.id)),
    },
    { key: 'displayName', label: 'Display name', width: 200, mobile: 'subtitle' },
    {
      key: 'type',
      label: 'Type',
      width: 104,
      // 1 = Sales, anything else = Stock (the attribute form's two types).
      render: cells.typePill((a, ctx) =>
        Number(a.type) === 1 ? t(ctx.language, 'Sales', 'Sales') : t(ctx.language, 'Stock', 'Stock')
      ),
      mobile: 'sheet',
    },
    {
      key: 'updatedAt',
      label: 'Last update',
      width: 130,
      render: cells.date(a => a.updatedAt),
      mobile: 'sheet',
    },
    {
      key: 'status',
      label: 'Status',
      width: 88,
      render: cells.status(a => !!a.isActive, { on: 'Active', off: 'Not Active' }),
      mobile: 'trailing',
    },
  ];

  const rowActions: ListRowAction<AttributeRow>[] = [
    {
      key: 'edit',
      label: 'Edit',
      icon: <EditIconV2 />,
      sheetIcon: 'inventory',
      onRun: a => goToAttribute(String(a.id)),
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteIconV2 />,
      sheetIcon: 'close',
      danger: true,
      onRun: a => handleShowDelete(String(a.id)),
    },
  ];

  return (
    <>
      {/*
        Desktop is a flex column so the list fills whatever height is left
        after the title row (same layout as Items.tsx).
      */}
      <div
        className="h-full relative"
        style={{
          overflowY: isMobile ? 'auto' : undefined,
          paddingBottom: isMobile ? '24px' : undefined,
          display: isMobile ? undefined : 'flex',
          flexDirection: isMobile ? undefined : 'column',
        }}
      >
        <div className="lg:flex items-center justify-between">
          <p
            style={{ color: '#222525' }}
            className="text-[24px] leading-[130%] font-medium font-inter"
          >
            {language && language?.['Attributes'] ? language?.['Attributes'] : 'Attributes'}
          </p>
          <div
            className="cursor-pointer flex items-center justify-center"
            style={{
              gap: '8px',
              padding: "8px 24px 8px 16px",
              backgroundColor: '#0F7679',
              borderRadius: '30px',
              width: 'fit-content',
              marginTop: isMobile ? '20px' : '0px',
            }}
            onClick={() => {
              webNavigate('/' + preLanguage + '/attributes/new');
            }}
          >
            <div style={{ width: '24px', height: '24px' }} className="flex items-center justify-center">
              <PlusIconWhite />
            </div>
            <p
              style={{ color: '#FFFFFF' }}
              className="text-[14px] leading-[130%] font-medium font-inter"
            >
              {language && language?.['Add attribute']
                ? language?.['Add attribute']
                : 'Add attribute'}
            </p>
          </div>
        </div>
        <ListScreen
          ref={listRef}
          language={language}
          columns={columns}
          fetchRows={fetchAttributes}
          searchPlaceholder="Search attributes"
          searchInputRef={searchInputRef}
          rowActions={rowActions}
          countNoun="attributes"
          emptyState={inforTable}
        />
      </div>
      <div
        className={`fixed w-full h-full top-0 left-0 flex items-center justify-center px-4 ${showDelete ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          } transition-opacity duration-300 ease-in-out`}
        style={{
          backgroundColor: '#22252552',
          zIndex: '200',
          transition: 'opacity 0.3s ease, visibility 0.3s ease',
          width: '100%',
        }}
      >
        <div
          className={`transform transition-transform duration-300 ease-in-out ${showDelete ? 'translate-y-0' : 'translate-y-4 opacity-0'
            }`}
          style={{
            padding: '24px',
            borderRadius: '24px',
            backgroundColor: '#FFFFFF',
            width: '530px',
            maxWidth: 'calc(100vw - 32px)',
            transition: 'transform 0.3s ease, opacity 0.3s ease',
          }}
        >
          <div className="flex items-center justify-between">
            <p
              style={{ fontSize: '20px' }}
              className="font-semibold font-inter text-main leading-[24px]"
            >
              {language && language?.['Delete attribute?']
                ? language?.['Delete attribute?']
                : 'Delete attribute?'}
            </p>
            <div
              onClick={() => setShowDelete(false)}
              className="flex items-center justify-center cursor-pointer"
              style={{
                width: '24px',
                height: '24px',
              }}
            >
              <CloseIcon />
            </div>
          </div>
          <div className="flex items-center justify-between" style={{ marginTop: '12px' }}>
            <p
              className="text-[14px] font-inter leading-[24px]"
              style={{ color: '#6A7878' }}
            >
              {language &&
                language?.[
                'This attribute will be permanently deleted. Are you sure you want to continue?'
                ]
                ? language?.[
                'This attribute will be permanently deleted. Are you sure you want to continue?'
                ]
                : 'This attribute will be permanently deleted. Are you sure you want to continue?'}
            </p>
            {/* <span className="text-main">[sale ID]</span> sale. */}
          </div>
          <div className="flex items-center justify-end" style={{ marginTop: '24px', gap: '12px' }}>
            <div
              onClick={() => setShowDelete(false)}
              className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
              style={{
                borderRadius: '30px',
                padding: '10px 24px',
                border: '1px solid #C9D7D8',
                color: '#222525',
              }}
            >
              {language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'}
            </div>
            <div
              onClick={handleDeleteEmployee}
              className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
              style={{
                borderRadius: '30px',
                padding: '10px 24px',
                backgroundColor: '#BA0E13',
                color: '#FFFFFF',
              }}
            >
              {language && language?.['Delete'] ? language?.['Delete'] : 'Delete'}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Attributes;
